import { useEffect, useState } from "react";
import uploadToCloudinary from "../utils/uploadToCloudinary";
import { collection, addDoc, getDocs, deleteDoc, doc } from "firebase/firestore";
import { db } from "../lib/firebase";

const AdminTeam = () => {
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [team, setTeam] = useState([]);

  const fetchTeam = async () => {
    const snap = await getDocs(collection(db, "team"));
    setTeam(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const addMember = async () => {
    if (!name || !role || !image) {
      alert("All fields required");
      return;
    }

    try {
      setLoading(true);
      const imageUrl = await uploadToCloudinary(image);

      await addDoc(collection(db, "team"), {
        name,
        role,
        imageUrl,
        createdAt: new Date()
      });

      setName("");
      setRole("");
      setImage(null);
      setPreview(null);
      fetchTeam();
    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  const deleteMember = async (id) => {
    if (!window.confirm("Delete this member?")) return;
    await deleteDoc(doc(db, "team", id));
    fetchTeam();
  };

  return (
    <div className="space-y-10">
      {/* HEADER */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Team Management</h1>
          <p className="text-gray-500">
            Add, manage, and showcase your team members
          </p>
        </div>
      </div>

      {/* ADD MEMBER CARD */}
      <div className="bg-white rounded-xl shadow-sm border p-6 max-w-2xl">
        <h2 className="text-xl font-semibold mb-4">Add New Member</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-black outline-none"
            placeholder="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <input
            className="border rounded-lg px-4 py-2 focus:ring-2 focus:ring-black outline-none"
            placeholder="Role (e.g. Designer)"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
        </div>

        {/* IMAGE UPLOAD */}
        <div className="mt-4 flex items-center gap-4">
          <label className="cursor-pointer bg-gray-100 px-4 py-2 rounded-lg border hover:bg-gray-200 transition">
            Upload Photo
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                setImage(file);
                setPreview(URL.createObjectURL(file));
              }}
            />
          </label>

          {preview && (
            <img
              src={preview}
              alt="Preview"
              className="w-14 h-14 rounded-full object-cover border"
            />
          )}
        </div>

        <button
          onClick={addMember}
          disabled={loading}
          className="mt-6 bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800 transition disabled:opacity-50"
        >
          {loading ? "Uploading..." : "Save Member"}
        </button>
      </div>

      {/* TEAM TABLE */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="p-4 text-left">Photo</th>
              <th className="p-4 text-left">Name</th>
              <th className="p-4 text-left">Role</th>
              <th className="p-4 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {team.map(member => (
              <tr
                key={member.id}
                className="border-t hover:bg-gray-50 transition"
              >
                <td className="p-4">
                  <img
                    src={member.imageUrl}
                    alt={member.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                </td>
                <td className="p-4 font-medium">{member.name}</td>
                <td className="p-4 text-gray-500">{member.role}</td>
                <td className="p-4 text-right">
                  <button
                    onClick={() => deleteMember(member.id)}
                    className="text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}

            {team.length === 0 && (
              <tr>
                <td colSpan="4" className="p-6 text-center text-gray-400">
                  No team members added yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminTeam;
